import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Upload, CheckCircle, XCircle, Loader2, DollarSign } from 'lucide-react';
import { submitAdmission, createCheckoutSession, getAdmissionCount } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import emailjs from "@emailjs/browser";

const Admissions = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [admissionCount, setAdmissionCount] = useState({ available_seats: 200 });
    const [formData, setFormData] = useState({
        student_name: '',
        father_name: '',
        mother_name: '',
        date_of_birth: '',
        grade: '',
        previous_school: '',
        address: '',
        city: '',
        state: 'Maharashtra',
        pincode: '',
        parent_email: '',
        parent_phone: '',
    });
    const [documents, setDocuments] = useState([]);

    useEffect(() => {
        const fetchCount = async () => {
            try {
                const data = await getAdmissionCount();
                setAdmissionCount(data);
            } catch (error) {
                console.error('Error fetching admission count:', error);
            }
        };
        fetchCount();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setDocuments(files);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const templateParams = {
                student_name: formData.student_name,
                father_name: formData.father_name,
                mother_name: formData.mother_name,
                date_of_birth: formData.date_of_birth,
                grade: formData.grade,
                previous_school: formData.previous_school,
                address: formData.address,
                city: formData.city,
                state: formData.state,
                pincode: formData.pincode,
                parent_email: formData.parent_email,
                parent_phone: formData.parent_phone,
                to_email: "rkenglishschool2014@gmail.com"
            };

            await emailjs.send(
                "service_ta5agz1",
                "template_khtupjj",
                templateParams,
                "LcSKq-P-Rebw0Cw5c"
            );

            toast.success("Admission form submitted successfully!");

            setCurrentStep(3);

            setFormData({
                student_name: '',
                father_name: '',
                mother_name: '',
                date_of_birth: '',
                grade: '',
                previous_school: '',
                address: '',
                city: '',
                state: 'Maharashtra',
                pincode: '',
                parent_email: '',
                parent_phone: '',
            });

            setDocuments([]);

        } catch (error) {
            console.error("EmailJS Error:", error);
            toast.error("Failed to submit form");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePaymentClick = async () => {
        if (!formData.student_name || !formData.grade || !formData.parent_email) {
            toast.error('Please fill in student name, grade, and parent email first');
            return;
        }

        setIsSubmitting(true);
        try {
            const gradePackageMap = {
                'Nursery': 'nursery',
                'LKG': 'nursery',
                'UKG': 'nursery',
                'Class 1': 'primary',
                'Class 2': 'primary',
                'Class 3': 'primary',
                'Class 4': 'primary',
                'Class 5': 'primary',
                'Class 6': 'middle',
                'Class 7': 'middle',
                'Class 8': 'middle',
                'Class 9': 'secondary',
                'Class 10': 'secondary',
                'Class 11': 'senior',
                'Class 12': 'senior',
            };

            const packageId = gradePackageMap[formData.grade] || 'primary';

            const response = await createCheckoutSession({
                package_id: packageId,
                student_name: formData.student_name,
                grade: formData.grade,
                parent_email: formData.parent_email,
            });

            if (response.url) {
                window.location.href = response.url;
            }
        } catch (error) {
            console.error('Error creating checkout session:', error);
            toast.error('Failed to initiate payment. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const grades = [
        'Nursery', 'LKG', 'UKG',
        'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
        'Class 6', 'Class 7', 'Class 8',
        'Class 9', 'Class 10',
        'Class 11 (Science)', 'Class 11 (Commerce)', 'Class 11 (Arts)',
        'Class 12 (Science)', 'Class 12 (Commerce)', 'Class 12 (Arts)',
    ];

    const feeStructure = [
        { grade: 'Nursery - UKG', fee: 'Contact Admin' },
        { grade: 'Class 1 - 5', fee: 'Contact Admin' },
        { grade: 'Class 6 - 8', fee: 'Contact Admin' },
        { grade: 'Class 9 - 10', fee: 'Contact Admin' },
        { grade: 'Class 11 - 12', fee: 'Contact Admin' },
    ];

    return (
        <div className="min-h-screen pt-24" data-testid="admissions-page">
            {/* Hero Section */}
            <section
                className="relative py-20 overflow-hidden"
                style={{
                    backgroundImage: `url(https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=1920)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-900/90 via-purple-800/90 to-blue-900/90"></div>
                <div className="relative z-10 container mx-auto px-4 md:px-8 text-center text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="font-outfit font-bold text-5xl md:text-7xl mb-6 text-white">Admissions 2026-2027</h1>
                        <p className="font-dm-sans text-xl md:text-2xl max-w-3xl mx-auto text-white/95">
                            Join our family of excellence - Limited seats available!
                        </p>
                        <div className="inline-block bg-gradient-to-r from-red-600 to-red-700 text-white px-8 py-4 rounded-full font-outfit font-bold text-lg mt-8 shadow-xl">
                            {admissionCount.available_seats} Seats Remaining
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Admission Process */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 md:px-8">
                    <h2 className="font-outfit font-bold text-4xl text-primary text-center mb-12">Admission Process</h2>
                    <div className="grid md:grid-cols-4 gap-6 max-w-5xl mx-auto">
                        {['Fill Application', 'Submit Documents', 'Pay Fees', 'Confirmation'].map((step, index) => (
                            <div key={index} className="text-center">
                                <div
                                    className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold border-4 ${currentStep > index + 1
                                        ? 'bg-green-600 text-white border-green-600'
                                        : currentStep === index + 1
                                            ? 'bg-gradient-to-br from-red-600 to-red-700 text-white border-red-600'
                                            : 'bg-white text-black-600 border-slate-300'
                                        }`}
                                >
                                    {currentStep > index + 1 ? '✓' : index + 1}
                                </div>
                                <p className="font-outfit font-semibold">{step}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Fee Structure */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4 md:px-8">
                    <h2 className="font-outfit font-bold text-4xl text-red-600 text-center mb-12">Fee Structure</h2>
                    <div className="max-w-3xl mx-auto bg-white rounded-3xl shadow-xl overflow-hidden border-2 border-slate-100">
                        <div className="bg-gradient-to-r from-purple-700 to-purple-900 text-white p-6">
                            <h3 className="font-outfit font-bold text-2xl text-center text-white">Academic Year 2026-2027</h3>
                        </div>
                        <div className="p-8">
                            {feeStructure.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between py-4 border-b border-slate-200 last:border-0"
                                >
                                    <span className="font-dm-sans text-lg text-black">{item.grade}</span>
                                    <a
                                        href="tel:09970657588"
                                        className="font-outfit font-bold text-xl text-purple-700 hover:text-red-600 underline"
                                    >
                                        {item.fee}
                                    </a>
                                </div>
                            ))}
                            <div className="mt-6 p-4 bg-slate-50 rounded-xl">
                                <p className="font-dm-sans text-sm text-slate-700">
                                    * Fees include tuition, library, sports, and lab charges. Transport and uniform are additional.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Application Form */}
            <section className="py-24 bg-white">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="max-w-4xl mx-auto">
                        <h2 className="font-outfit font-bold text-4xl text-center mb-4 text-black">
                            Online Admission Form
                        </h2>
                        <p className="text-center text-muted-foreground mb-12 text-slate-800">
                            Fill in the details below to start the admission process
                        </p>

                        <form onSubmit={handleSubmit} className="glass rounded-3xl p-8 md:p-12" data-testid="admission-form">
                            {/* Student Information */}
                            <div className="mb-10">
                                <h3 className="font-outfit font-bold text-2xl mb-6 text-black">Student Information</h3>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <Label htmlFor="student_name">Student Name *</Label>
                                        <Input
                                            id="student_name"
                                            name="student_name"
                                            value={formData.student_name}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="student-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="date_of_birth">Date of Birth *</Label>
                                        <Input
                                            id="date_of_birth"
                                            name="date_of_birth"
                                            type="date"
                                            value={formData.date_of_birth}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="dob-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="grade">Grade Applying For *</Label>
                                        <Select
                                            value={formData.grade}
                                            onValueChange={(value) => setFormData((prev) => ({ ...prev, grade: value }))}
                                            required
                                        >
                                            <SelectTrigger data-testid="grade-select">
                                                <SelectValue placeholder="Select Grade" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {grades.map((grade) => (
                                                    <SelectItem key={grade} value={grade}>
                                                        {grade}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label htmlFor="previous_school">Previous School</Label>
                                        <Input
                                            id="previous_school"
                                            name="previous_school"
                                            value={formData.previous_school}
                                            onChange={handleInputChange}
                                            data-testid="previous-school-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Parent Information */}
                            <div className="mb-10">
                                <h3 className="font-outfit font-bold text-2xl mb-6 text-black">Parent Information</h3>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <Label htmlFor="father_name">Father's Name *</Label>
                                        <Input
                                            id="father_name"
                                            name="father_name"
                                            value={formData.father_name}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="father-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="mother_name">Mother's Name *</Label>
                                        <Input
                                            id="mother_name"
                                            name="mother_name"
                                            value={formData.mother_name}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="mother-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="parent_email">Parent Email *</Label>
                                        <Input
                                            id="parent_email"
                                            name="parent_email"
                                            type="email"
                                            value={formData.parent_email}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="parent-email-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="parent_phone">Parent Phone *</Label>
                                        <Input
                                            id="parent_phone"
                                            name="parent_phone"
                                            type="tel"
                                            value={formData.parent_phone}
                                            onChange={handleInputChange}
                                            required
                                            data-testid="parent-phone-input"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div className="mb-10">
                                <h3 className="font-outfit font-bold text-2xl mb-6 text-black">Address Details</h3>
                                <div className="grid gap-6">
                                    <div>
                                        <Label htmlFor="address">Full Address *</Label>
                                        <Textarea
                                            id="address"
                                            name="address"
                                            value={formData.address}
                                            onChange={handleInputChange}
                                            required
                                            rows={3}
                                            data-testid="address-input"
                                        />
                                    </div>
                                    <div className="grid md:grid-cols-3 gap-6">
                                        <div>
                                            <Label htmlFor="city">City *</Label>
                                            <Input
                                                id="city"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleInputChange}
                                                required
                                                data-testid="city-input"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="state">State *</Label>
                                            <Input
                                                id="state"
                                                name="state"
                                                value={formData.state}
                                                onChange={handleInputChange}
                                                required
                                                data-testid="state-input"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="pincode">Pincode *</Label>
                                            <Input
                                                id="pincode"
                                                name="pincode"
                                                value={formData.pincode}
                                                onChange={handleInputChange}
                                                required
                                                data-testid="pincode-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Document Upload */}
                            <div className="mb-10">
                                <h3 className="font-outfit font-bold text-2xl mb-6 text-black">Upload Documents</h3>
                                <div className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-red-600 transition-colors">
                                    <Upload className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                                    <Label htmlFor="documents" className="cursor-pointer">
                                        <span className="font-outfit font-semibold text-black hover:underline">
                                            Click to upload
                                        </span>
                                        <span className="text-black"> or drag and drop</span>
                                        <p className="text-sm text-slate-600 mt-2">
                                            Birth certificate, previous school records, photos (Max 5 files)
                                        </p>
                                    </Label>
                                    <Input
                                        id="documents"
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                        className="hidden"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        data-testid="document-upload-input"
                                    />
                                    {documents.length > 0 && (
                                        <div className="mt-4 text-left">
                                            <p className="font-semibold mb-2">Selected files:</p>
                                            <ul className="text-sm text-muted-foreground space-y-1">
                                                {documents.map((file, index) => (
                                                    <li key={index}>• {file.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-4 justify-center border-black">
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-8 py-6 text-lg"
                                    data-testid="submit-admission-button"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <CheckCircle className="mr-2 h-5 w-5 text-black" />
                                            <p className="text-red">Submit Application</p>
                                        </>
                                    )}
                                </Button>
                                {/* <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handlePaymentClick}
                                    disabled={isSubmitting}
                                    className="px-8 py-6 text-lg"
                                    data-testid="pay-fees-button"
                                >
                                    <DollarSign className="mr-2 h-5 w-5" />
                                    Pay Admission Fees Online
                                </Button> */}
                            </div>
                        </form>

                        {currentStep === 3 && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="mt-8 glass rounded-2xl p-8 text-center"
                            >
                                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                                <h3 className="font-outfit font-bold text-2xl text-primary mb-2">
                                    Application Submitted Successfully!
                                </h3>
                                <p className="font-dm-sans text-muted-foreground">
                                    We have received your application. Our team will contact you within 2-3 business days.
                                </p>
                            </motion.div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Admissions;
